import { useRef, useEffect, useState } from "react";
import { TokenCard, TokenCardRef } from "@allaria-tech/matter/TokenCard";
import {
  onUpdatePrimaryButton,
  onUpdateSecondaryButton,
} from "../../reducers/buttonsReducer";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  authCoownerToken,
  authSignatoryToken,
  authToken,
  regenerateToken,
} from "../../connectors/connectors";
import { updateToken } from "../../reducers/authReducer";
import { authorizeDraft, getDraftUUIDFromToken } from "../../app/utils";
import { onUpdateDraftUUID } from "../../reducers/onboardingDataReducer";
import { FormikProps, FormikValues } from "formik";
import useForm from "../../hooks/useForm";
import { getPartnerCompanyType, minutesToMiliseconds } from "../../utils";
import { getCompanyData } from "../../utils/companies";

import styles from "./TokenStep.module.scss";

const maskEmail = (email: string) => {
  const [user, domain] = email.split("@");
  const maskedChars = Math.max(
    1,
    Math.min(email.length > 20 ? 8 : 3, user.length - 1)
  );
  return `${user.slice(0, -maskedChars)}${"*".repeat(maskedChars)}@${domain}`;
};

interface TokenStepProps {
  onSuccess: () => Promise<void>;
  onAuthorize: (draftUUID?: string) => Promise<void>;
  previousStep?: number;
  formik: FormikProps<FormikValues>;
  oldAuth?: boolean;
}
const TokenStep = ({
  onSuccess,
  onAuthorize,
  previousStep,
  formik,
  oldAuth,
}: TokenStepProps) => {
  const tokenCardRef = useRef<TokenCardRef>(null);
  const dispatch = useAppDispatch();
  const { handleBack } = useForm(formik);
  const { signatureRequestId } = useAppSelector(
    (state) => state.authentication,
  );
  const onboardingData = useAppSelector((state) => state.onboardingData);

  const {
    stepData: { step },
    formValues,
  } = onboardingData;

  const { onboardingType } = useAppSelector((state) => state.activeTemplate);

  const partnerCompanyType = formValues && getPartnerCompanyType(formValues);

  const [loading, setLoading] = useState(false);
  const [lastTokenSent, setLastTokenSent] = useState<number>(Date.now());

  const handleAuthorize = () => {
    if (tokenCardRef.current) {
      tokenCardRef?.current?.authorizeToken();
    }
  };

  const sendToken = async ({
    signatureRequestId,
    token,
  }: {
    signatureRequestId: number;
    token: string;
  }) => {
    switch (onboardingType) {
      case "COOWNER":
        return await authToken({
          email: formValues?.email,
          signatureRequestId,
          token: token,
        });
      case "ADDITIONAL_COOWNER":
        return await authCoownerToken({
          email: formValues?.email,
          signatureRequestId,
          token: token,
          externalCode: onboardingData.draftUUID,
        });
      case "SIGNATORY":
        return await authSignatoryToken({
          email: formValues?.email,
          signatureRequestId,
          token: token,
          externalCode: onboardingData.draftUUID,
        });
      default:
        return await authToken({
          email: formValues?.email,
          signatureRequestId,
          token: token,
        });
    }
  };

  useEffect(() => {
    dispatch(
      onUpdatePrimaryButton({
        onClick: handleAuthorize,
        text: "Continuar",
        type: "button",
        disabled: loading,
        isLoading: loading,
        show: true,
      }),
    );
    if (previousStep)
      dispatch(
        onUpdateSecondaryButton({
          text: "Volver",
          disabled: false,
          isLoading: false,
          onClick: handleBack,
          show: true,
        }),
      );
  }, [loading, previousStep]);

  useEffect(() => {
    dispatch(
      onUpdatePrimaryButton({
        onClick: handleAuthorize,
        text: "Continuar",
        type: "button",
        disabled: true,
        isLoading: false,
        show: true,
      }),
    );
    if (previousStep)
      dispatch(
        onUpdateSecondaryButton({
          text: "Volver",
          disabled: false,
          isLoading: false,
          onClick: handleBack,
          show: true,
        }),
      );
  }, []);

  return (
    <div className={styles.tokenStepContainer}>
      <TokenCard
        ref={tokenCardRef}
        inline
        title="Validá tu dirección de mail"
        autoSendToken
        lastSendToken={lastTokenSent + minutesToMiliseconds(2)}
        onAuthorize={async (token) => {
          setLoading(true);
          try {
            if (!signatureRequestId)
              throw new Error("Error: signatureRequestId not found");

            const authResponse = await sendToken({
              signatureRequestId,
              token: token,
            });

            dispatch(updateToken(authResponse.data.token));

            const draftUUID = getDraftUUIDFromToken({
              token: authResponse.data.token,
            });

            dispatch(onUpdateDraftUUID(draftUUID));

            await authorizeDraft(
              async () => {
                await onAuthorize?.(draftUUID);
              },
              async () => {
                await onAuthorize?.(draftUUID);
                await onSuccess();
              },
            );

            return {
              success: true,
            };
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } catch (error: any) {
            setLoading(false);
            if (error.response?.data?.code === "INVALID_TOKEN") {
              return {
                success: false,
                message:
                  "El token ingresado es incorrecto. Verificalo e intentá nuevamente.",
              };
            } else if (error.response?.data?.code === "EXPIRED_TOKEN") {
              return {
                success: false,
                message:
                  "El token ha expirado. Solicitá uno nuevo para continuar.",
              };
            } else if (error.response?.data?.code === "MAX_ATTEMPTS_REACHED") {
              return {
                success: false,
                message:
                  "Superaste el número máximo de intentos permitidos. Por seguridad, generá un nuevo token.",
              };
            } else
              return {
                success: false,
                message: "Error al autorizar el token.",
              };
          }
        }}
        onResend={async () => {
          if (!signatureRequestId || !formValues?.email) return;

        try {
          await regenerateToken({
            email: formValues?.email,
            signatureRequestId,
            companyType: getCompanyData().companyName,
            oldAuth,
            es: true,
            ...(partnerCompanyType ? { partnerCompanyType } : {}),
          });

            setLastTokenSent(Date.now());
          } catch (error) {
            console.error(error);
          }
        }}
        onSuccess={() => {
          if (!step) return;
          onSuccess && onSuccess();
          setLoading(false);
        }}
      >
        Ingresá el token que enviamos a {maskEmail(formValues?.email)}. El
        código es válido durante 24 horas
      </TokenCard>
    </div>
  );
};

export default TokenStep;
